import {StyledPopover} from '@components/commons/date-picker/style.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {
    StyledCalendarTriggerButton,
    StyledFilterDatePickerFooter,
    StyledFilterDatePickerGrid,
    StyledOptionsWrapper,
    StyledSelectRangeButton
} from '@/components/commons/range-date-picker-legacy/style'
import {getDateFromLabel, ObjectKeys} from '@utilities/helpers.ts'
import {CalendarIcon, CheckIcon} from '@components/ui/icon/Icon.tsx'
import {PopoverDisclosure, PopoverProvider, usePopoverContext, usePopoverStore} from '@ariakit/react'
import {useTranslation} from '@/translations/i18n'
import {rangeDateToLabel} from '@/utilities/utils'
import {RangeCalendarPicker} from '../calendar/range-calendar-picker/RangeCalendarPicker'
import {useCallback, useState, useMemo, FC} from 'react'
import {DateRange} from 'react-day-picker'
import dayjs, {formatDate} from '@/dayjs'
interface RangeDatePickerProps {
    values: DateRange
    disabled?: boolean
    onClear: () => void
    onConfirm: (values: DateRange) => void
    canSelectOneDay?: boolean
}
export const RangeDatePicker = ({values, onClear, onConfirm, disabled, canSelectOneDay}: RangeDatePickerProps) => {
    const {t} = useTranslation()
    const popover = usePopoverStore()

    return (
        <PopoverProvider store={popover} placement="bottom-start">
            <PopoverDisclosure
                render={
                    <StyledCalendarTriggerButton disabled={disabled} variant="secondary">
                        <CalendarIcon />
                        {values?.from && values?.to
                            ? t(`users:from_to_date`, {
                                  startDate: formatDate(new Date(values.from).toISOString(), 'DD/MM/YYYY'),
                                  endDate: formatDate(new Date(values.to).toISOString(), 'DD/MM/YYYY')
                              })
                            : t('patients:date_period')}
                    </StyledCalendarTriggerButton>
                }
            />
            <StyledPopover gutter={8} unmountOnHide>
                <RangeDatePickerPopoverContent
                    values={values}
                    onClear={onClear}
                    onConfirm={onConfirm}
                    canSelectOneDay={canSelectOneDay}
                />
            </StyledPopover>
        </PopoverProvider>
    )
}

const RangeDatePickerPopoverContent: FC<RangeDatePickerProps> = ({values, onClear, onConfirm, canSelectOneDay}) => {
    const {t} = useTranslation()
    const popover = usePopoverContext()
    const [selected, setSelected] = useState<DateRange>(values)

    const filteredRangeDateToLabel = useMemo(() => {
        if (!canSelectOneDay) {
            return Object.fromEntries(
                Object.entries(rangeDateToLabel).filter(([key]) => key !== 'today' && key !== 'yesterday')
            )
        }
        return rangeDateToLabel
    }, [canSelectOneDay])

    const handleClear = useCallback(() => {
        setSelected({from: undefined, to: undefined})
        onClear()
        popover?.setOpen(false)
    }, [onClear])

    const handleConfirm = useCallback(() => {
        if (!canSelectOneDay && selected.from && selected.to) {
            if (dayjs(selected.from).isSame(dayjs(selected.to), 'day')) {
                return
            }
        }
        onConfirm(selected)
        popover?.setOpen(false)
    }, [onConfirm, selected])
    return (
        <StyledFilterDatePickerGrid>
            <StyledOptionsWrapper>
                {ObjectKeys(filteredRangeDateToLabel).map(option => {
                    const startDate = getDateFromLabel(option).startDate
                    const endDate = getDateFromLabel(option).endDate
                    const isSelected =
                        selected.from && selected.to
                            ? dayjs(selected.from).isSame(dayjs(startDate), 'day') &&
                              dayjs(selected.to).isSame(dayjs(endDate), 'day')
                            : false
                    return (
                        <StyledSelectRangeButton
                            key={option}
                            variant={'ghost'}
                            isSelected={isSelected}
                            onClick={() => {
                                if (startDate && endDate) {
                                    setSelected({from: new Date(startDate), to: new Date(endDate)})
                                } else {
                                    handleClear()
                                }
                            }}
                        >
                            {t(filteredRangeDateToLabel[option])}
                            {isSelected && <CheckIcon size={16} />}
                        </StyledSelectRangeButton>
                    )
                })}
            </StyledOptionsWrapper>
            <Flexbox direction={'column'} fullWidth>
                <RangeCalendarPicker onSelect={setSelected} selected={selected} />
                <StyledFilterDatePickerFooter>
                    <Flexbox justify="space-between" fullWidth>
                        <Button
                            variant={'ghost'}
                            onClick={() => {
                                popover?.setOpen(false)
                            }}
                        >
                            {t('commons:cancel')}
                        </Button>
                        <Flexbox gap={3}>
                            <Button variant={'secondary'} onClick={handleClear}>
                                {t('commons:clear_search')}
                            </Button>
                            <Button variant="primaryNeutral" onClick={handleConfirm}>
                                {t('commons:confirm')}
                            </Button>
                        </Flexbox>
                    </Flexbox>
                </StyledFilterDatePickerFooter>
            </Flexbox>
        </StyledFilterDatePickerGrid>
    )
}
